import type { Amount, ClaimTransmissionStatusType } from '../../api';
import type { RosterApiPage } from './roster.api-page';
import type { RosterClaim, RosterClaimStatus } from './roster.types';

/**
 * The fields of a support or provider-app claim the Roster's "apply to existing claims" picker
 * shows — both `SupportClaim` and `ProviderClaim` carry them under the same names.
 */
export interface RosterApiClaim {
    id?: string;
    fromDos?: string;
    patientFirst?: string;
    patientLast?: string;
    primaryPayerName?: string;
    chargeAmount?: Amount;
    transmissionStatus?: ClaimTransmissionStatusType;
}

/**
 * A claim as the picker shows it. The status is the API's transmission status as is — `NOT_SENT`
 * when there is none — not mapped onto the design's words (NS-3). A blank patient or payer stays
 * blank rather than being filled in (NS-4).
 */
export function rosterClaimFromApi(claim: RosterApiClaim): RosterClaim {
    return {
        id: claim.id as string,
        dos: (claim.fromDos ?? '').slice(0, 10),
        patientName: [claim.patientFirst, claim.patientLast].filter(Boolean).join(' '),
        payerName: claim.primaryPayerName ?? '',
        chargeAmount: claim.chargeAmount?.amount ?? '',
        status: (claim.transmissionStatus as RosterClaimStatus | undefined) ?? 'NOT_SENT',
    };
}

/** More claims than this for one record is not a picker; the operator narrows by date instead. */
export const ROSTER_CLAIMS_CAP = 1000;

/**
 * Every claim that references a record, paged 100 at a time (the API's limit). Past the cap it
 * refuses rather than showing a subset as "all claims" (NS-3).
 */
export async function rosterCollectClaims(
    fetchPage: (offset: number, limit: number) => Promise<RosterApiPage<RosterApiClaim>>,
): Promise<RosterClaim[]> {
    const claims: RosterClaim[] = [];
    for (let offset = 0; ; offset += 100) {
        const page = await fetchPage(offset, 100);
        if (page.total > ROSTER_CLAIMS_CAP) {
            throw new Error(`${page.total} claims reference this record — more than ${ROSTER_CLAIMS_CAP} can be listed here.`);
        }
        claims.push(...page.items.map(rosterClaimFromApi));
        if (page.items.length === 0 || claims.length >= page.total) {
            return claims;
        }
    }
}
